import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, Fragment as _Fragment, unref as _unref, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = {
  key: 0,
  class: "req",
  "aria-hidden": "true"
}
const _hoisted_3 = ["for"]
const _hoisted_4 = {
  key: 0,
  class: "req",
  "aria-hidden": "true"
}
const _hoisted_5 = {
  key: 2,
  class: "rd-relative"
}
const _hoisted_6 = ["id"]

import { FieldStyle } from "@/builder/enums";
import { useRenderStore } from '@/builder/stores/renderStore';
import { Ref, computed, inject, onBeforeMount, onMounted, ref, watch } from 'vue';
import { PageDesign } from '../page/PageInterface';


export default /*@__PURE__*/_defineComponent({
  __name: 'ValidatedInput',
  props: [
    'id',
    'value',
    'label',
    'isHeading',
    'accessibleLabel',
    'labelFor',
    'labelClasses',
    'isRequired',
    'isEmail',
    'name',
    'maxLength',
    'isInvalid',
    'describedBy',
    'displayName',
    'isStepError'
],
  setup(__props, { expose: __expose }) {

const renderStore = useRenderStore();
const pageDesign = inject("pageDesign") as Ref<PageDesign>;
const fieldStyle = computed(() => pageDesign.value.fieldStyle);

const props = __props;

// refs
const error = ref<string | null>(null);
const timeout = ref<any>(null);

// computed
const getAriaDescribedBy = computed(() => error.value ? `errorMessage_` + getHtmlId.value : null);
const getAriaIsInvalid = computed(() => error.value ? true : false);
const getHtmlId = computed(() => props.id + '_' + props.name);
const getLabelText = computed(() => props.label ? props.label : props.accessibleLabel);

// methods
const validate = (val: string): void => {
    if (props.isRequired && !props.value) {
        const inputName = props.displayName ? props.displayName : props.label;
        if (inputName.length > 0) {
            error.value = `${inputName} is required.`;
        } else {
            error.value = "This field is required.";
        }
    } else if (props.isEmail) {
        const isEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!isEmailRegex.test(val)) {
            error.value = "Please enter a valid email address.";
        } else {
            error.value = null;
        }
    } else if (props.maxLength && props.value && props.value.length > props.maxLength) {
        error.value = `Please enter no more than ${props.maxLength} characters.`;
    } else {
        error.value = null;
    }
};

const setModelState = (modelState): void => {
    const errorMsg = modelState[props.name];
    if (errorMsg) {
        error.value = Array.isArray(errorMsg) ? errorMsg[0] : errorMsg;
    }

    const errorLower = modelState[props.name.toLowerCase()];
    if (errorLower) {
        error.value = Array.isArray(errorLower) ? errorLower[0] : errorLower;
    }
};

const setError = (err: string | null): void => {
    error.value = err;
};

// expose
__expose({ setError, validate });

// lifecycle hooks
onMounted(() => {
    setModelState(renderStore.modelState);
});

onBeforeMount(() => {
    if (error.value) {
        const index = renderStore.errors.findIndex(x => x.propertyId === getHtmlId.value);
        if (index > -1) renderStore.errors.splice(index, 1);
    }
});

// watch
watch(() => props.value, (val) => {
    if (error.value) {
        error.value = null;
    }

    if (timeout.value) {
        clearTimeout(timeout.value);
    }

    timeout.value = setTimeout(() => {
        validate(val);
    }, 500);
});

watch(error, (newValue, oldValue) => {
    if (oldValue) {
        const index = renderStore.errors.findIndex(x => x.propertyId === getHtmlId.value);
        if (index > -1) renderStore.errors.splice(index, 1);
    }
    if (newValue) {
        const formStep = renderStore.getFormStep(props.id);
        if (formStep !== null) {
            renderStore.errors.push({ componentId: props.id, propertyId: getHtmlId.value, error: newValue, step: formStep });
        }
    }
});

watch(() => renderStore.modelState, (modelState) => {
    setModelState(modelState);
});

watch(() => renderStore.validateInputStep, (validateInputStep) => {
    if (validateInputStep === null) { return; }
    const parentStep = renderStore.getFormStep(props.id);
    if (parentStep != null && validateInputStep === parentStep) {
        validate(props.value);
    }
});

watch(() => renderStore.validating, (validating) => {
    if (validating) {
        validate(props.value);
    }
});

return (_ctx: any,_cache: any) => {
  return (__props.isStepError)
    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
        _renderSlot(_ctx.$slots, "input", { describedBy: getAriaDescribedBy.value }),
        (error.value)
          ? (_openBlock(), _createElementBlock("span", {
              key: 0,
              id: `errorMessage_` + getHtmlId.value,
              class: "input-msg input-msg-error"
            }, [
              _cache[0] || (_cache[0] = _createElementVNode("svg", {
                xmlns: "http://www.w3.org/2000/svg",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", { d: "M12.8659 3.00017L22.3922 19.5002C22.6684 19.9785 22.5045 20.5901 22.0262 20.8662C21.8742 20.954 21.7017 21.0002 21.5262 21.0002H2.47363C1.92135 21.0002 1.47363 20.5525 1.47363 20.0002C1.47363 19.8246 1.51984 19.6522 1.60761 19.5002L11.1339 3.00017C11.41 2.52187 12.0216 2.358 12.4999 2.63414C12.6519 2.72191 12.7782 2.84815 12.8659 3.00017ZM10.9999 16.0002V18.0002H12.9999V16.0002H10.9999ZM10.9999 9.00017V14.0002H12.9999V9.00017H10.9999Z" })
              ], -1)),
              _createElementVNode("span", null, _toDisplayString(error.value), 1)
            ], 8, _hoisted_1))
          : _createCommentVNode("", true)
      ], 64))
    : (_openBlock(), _createElementBlock("div", {
        key: 1,
        class: _normalizeClass(["form-field-container", [{ 'has-error': error.value },
    { 'form-underline': fieldStyle.value === _unref(FieldStyle).Underline },
    { 'form-compact': fieldStyle.value === _unref(FieldStyle).Compact }]])
      }, [
        (__props.isHeading)
          ? (_openBlock(), _createElementBlock("span", {
              key: 0,
              class: _normalizeClass(["label", __props.labelClasses])
            }, [
              _createTextVNode(_toDisplayString(getLabelText.value), 1),
              (__props.isRequired)
                ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
                : _createCommentVNode("", true)
            ], 2))
          : (_openBlock(), _createElementBlock("label", {
              key: 1,
              for: __props.labelFor ? __props.labelFor : getHtmlId.value,
              class: _normalizeClass([{ 'rd-sr-only': !__props.label }, __props.labelClasses])
            }, [
              _createTextVNode(_toDisplayString(getLabelText.value), 1),
              (__props.isRequired)
                ? (_openBlock(), _createElementBlock("span", _hoisted_4, "*"))
                : _createCommentVNode("", true)
            ], 10, _hoisted_3)),
        (fieldStyle.value == _unref(FieldStyle).Underline)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _renderSlot(_ctx.$slots, "input", {
                describedBy: getAriaDescribedBy.value,
                required: __props.isRequired,
                isInvalid: getAriaIsInvalid.value
              }),
              _cache[1] || (_cache[1] = _createElementVNode("span", { class: "border-input-focus" }, null, -1))
            ]))
          : _renderSlot(_ctx.$slots, "input", {
              key: 3,
              describedBy: getAriaDescribedBy.value,
              required: __props.isRequired,
              isInvalid: getAriaIsInvalid.value
            }),
        _renderSlot(_ctx.$slots, "extraContent"),
        (error.value)
          ? (_openBlock(), _createElementBlock("span", {
              key: 4,
              id: `errorMessage_` + getHtmlId.value,
              class: "input-msg input-msg-error"
            }, [
              _cache[2] || (_cache[2] = _createElementVNode("svg", {
                xmlns: "http://www.w3.org/2000/svg",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", { d: "M12.8659 3.00017L22.3922 19.5002C22.6684 19.9785 22.5045 20.5901 22.0262 20.8662C21.8742 20.954 21.7017 21.0002 21.5262 21.0002H2.47363C1.92135 21.0002 1.47363 20.5525 1.47363 20.0002C1.47363 19.8246 1.51984 19.6522 1.60761 19.5002L11.1339 3.00017C11.41 2.52187 12.0216 2.358 12.4999 2.63414C12.6519 2.72191 12.7782 2.84815 12.8659 3.00017ZM10.9999 16.0002V18.0002H12.9999V16.0002H10.9999ZM10.9999 9.00017V14.0002H12.9999V9.00017H10.9999Z" })
              ], -1)),
              _createElementVNode("span", null, _toDisplayString(error.value), 1)
            ], 8, _hoisted_6))
          : _createCommentVNode("", true)
      ], 2))
}
}

})